import { useState, useEffect, useMemo } from "react";
import Spinner from "../Spinner/Spinner"; // Make sure you have this component for loading indication
import useCRUD from "../../hooks/useCRUD";
import AddOrEditParticipantModal from "./AddorEditParticipantModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { formatPhoneNumber } from "../../utils/phoneNumberUtils";

const DriversList = ({ event, eventParticipations, refreshData }) => {
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: "availability",
    direction: "ascending",
  });

  const { addItem, updateItem } = useCRUD("/api/participant");
  const { updateItem: updateEventParticipation } = useCRUD(
    "/api/event-participations",
  );

  const messageDriver = async (driver) => {
    const message = window.prompt(
      `Personal message for ${driver.name}`,
      driver.messageFromAdmin || "",
    );
    if (message === null) return;
    await updateEventParticipation(driver.id, { messageFromAdmin: message });
    refreshData?.();
  };

  const toggleDriverGuide = async (driver) => {
    const showGuide = !driver.showPickupPageOnboarding;
    if (driver.showPickupPageOnboarding && driver.pickupPageOnboardingViewedAt) {
      await updateEventParticipation(driver.id, { showPickupPageOnboarding: false });
      await updateEventParticipation(driver.id, { showPickupPageOnboarding: true });
    } else {
      await updateEventParticipation(driver.id, {
        showPickupPageOnboarding: showGuide,
      });
    }
    refreshData?.();
  };

  const sortedDrivers = useMemo(() => {
    let sortableItems = [...drivers];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [drivers, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    if (eventParticipations) {
      //Filer to only show participants that are not drivers

      const filteredParticipants = eventParticipations.filter(
        (participant) => participant.isDriver,
      );
      setDrivers(filteredParticipants);
    }
  }, [eventParticipations]);

  const handleSave = async (data) => {
    try {
      setIsLoading(true);
      if (data.id) {
        // If data has an id, we're editing an existing participant
        await updateItem(data.id, data);
      } else {
        // If modalData does not exist, we're adding a new participant
        await addItem(data);
      }
      console.log("Participant saved successfully:", data);
      // Optionally, refresh your participants list here
    } catch (error) {
      console.error("Error saving participant:", error);
    } finally {
      setIsModalOpen(false); // Close the modal after save
      setIsLoading(false);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="card w-full max-w-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
      {isLoading && <Spinner />}

      <div className="p-3 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#004a7e]">
              Driver coordination
            </p>
            <h3 className="text-xl font-black text-gray-900">
              {event?.pingSchedule
                ? "Driver Responses"
                : `${drivers?.length} Event Drivers`}
            </h3>
          </div>
          {/* <button
            className="btn bg-[#004a7e] text-white mt-4 hover:bg-[#004a7e] hover:text-white btn-sm"
            onClick={handleAddNewDriverClick}
          >
            + Add New Driver
          </button> */}
        </div>

        {isModalOpen && (
          <AddOrEditParticipantModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={handleSave}
            initialData={null}
            isDriver={true}
            event={event}
          />
        )}

        {drivers.length > 0 ? (
          <>
          <div className="space-y-3 md:hidden">
            {sortedDrivers.map((driver) => (
              <div key={driver.id} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <a
                      className="font-black text-[#004a7e] underline"
                      href={`${event?.eventSignUpType === "Shuttle Event" ? "/r" : "/d"}/${driver.uniqueCode}?adminPreview=1`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {driver.name}
                    </a>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatPhoneNumber(driver.phone)} · Capacity {driver.canTake || "—"}
                    </p>
                  </div>
                  <span className={`badge ${driver.availability === "Yes" ? "badge-success text-white" : "badge-warning"}`}>
                    {driver.availability}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`badge ${driver.pickupNotificationSent ? "badge-info text-white" : "badge-warning"}`}>
                    Notification {driver.pickupNotificationSent ? "sent" : "pending"}
                  </span>
                  <button type="button" className="btn btn-xs" onClick={() => toggleDriverGuide(driver)}>
                    {driver.showPickupPageOnboarding ? "Guide on" : "Guide off"}
                  </button>
                  <button type="button" className="btn btn-xs" onClick={() => messageDriver(driver)}>
                    {driver.messageFromAdmin ? "Edit message" : "Add message"}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden w-full max-w-full overflow-x-auto md:block">
            <table className="table w-full">
              <thead>
                <tr>
                  <th onClick={() => requestSort("name")}>
                    Driver{" "}
                    {sortConfig.key === "name" &&
                      (sortConfig.direction === "ascending" ? "▲" : "▼")}
                  </th>
                  <th className="hidden md:table-cell"> Phone</th>
                  <th onClick={() => requestSort("canTake")}>
                    Can Take{" "}
                    {sortConfig.key === "canTake" &&
                      (sortConfig.direction === "ascending" ? "▲" : "▼")}
                  </th>
                  <th onClick={() => requestSort("availability")}>
                    Availability{" "}
                    {sortConfig.key === "availability" &&
                      (sortConfig.direction === "ascending" ? "▲" : "▼")}
                  </th>
                  <th>Pickup Notification</th>
                  <th>Driver Guide</th>
                  <th>Personal Message</th>
                  <th>Message To Admin</th>
                </tr>
              </thead>
              <tbody>
                {sortedDrivers.map((driver) => (
                  <tr key={driver.id}>
                    <td>
                      <div className="font-bold">
                        <a
                          href={`${event?.eventSignUpType === "Shuttle Event" ? "/r" : "/d"}/${driver.uniqueCode}?adminPreview=1`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {driver.name}
                        </a>
                        {driver.urlLastViewedAt && (
                          <span
                            className="tooltip ml-1"
                            data-tip={new Date(
                              driver.urlLastViewedAt,
                            ).toLocaleString()}
                          >
                            <FontAwesomeIcon
                              icon={faCircleCheck}
                              className="text-blue-500"
                            />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="hidden md:table-cell">
                      <div className="text-gray-500">
                        {formatPhoneNumber(driver.phone)}
                      </div>
                    </td>
                    <td>{driver.canTake}</td>
                    <td>
                      <div
                        className={`badge ${
                          driver.availability === "Yes"
                            ? "badge-success text-white"
                            : driver.availability === "No"
                              ? "badge-error  text-white"
                              : "badge-warning  text-white"
                        }`}
                      >
                        {driver.availability}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge ${driver.pickupNotificationSent ? "badge-info text-white" : "badge-warning"}`}
                      >
                        {driver.pickupNotificationSent ? "Sent" : "Pending"}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="badge badge-outline whitespace-nowrap"
                        onClick={() => toggleDriverGuide(driver)}
                      >
                        {driver.showPickupPageOnboarding &&
                        !driver.pickupPageOnboardingViewedAt
                          ? "Guide on"
                          : driver.showPickupPageOnboarding
                            ? "Show again"
                            : "Guide off"}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-xs whitespace-nowrap"
                        onClick={() => messageDriver(driver)}
                      >
                        {driver.messageFromAdmin ? "Edit message" : "Add message"}
                      </button>
                    </td>
                    <td>{driver.messageForAdmin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        ) : (
          <p className="rounded-lg bg-gray-50 p-5 text-center text-gray-600">
            No drivers to display.
          </p>
        )}
      </div>
    </div>
  );
};

export default DriversList;
